<template>
  <ontos-dialog :model-value="true" :fullscreen="$vuetify.display.smAndDown" max-width="960px"
            persistent>
    <!-- Direto dentro do diálogo: o painel que o formulário traz põe o cinza de "gravando" como
         irmão do card, e uma div em volta viraria o ancestral posicionado dele. -->
    <ontos-instance-create :context="context"
                           :initial-name="initialName"
                           :initial-values="initialValues"
                           :fixed-values="fixedValues"
                           :known="known"
                           :deferred="deferred"
                           :omit-properties="omitProperties"
                           @saved="onSaved"
                           @pending="$emit('pending', $event)"
                           @showMessage="$emit('showMessage', $event)"
                           @showErrorMessage="$emit('showErrorMessage', $event)">
      <template #controls="{dirty}">
        <ontos-dialog-actions :full-page-route="fullPageRoute" :dirty="dirty"
                              @closed="$emit('closed')"/>
      </template>
    </ontos-instance-create>
  </ontos-dialog>
</template>
<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import InstanceCreate from '@/components/live/instance/InstanceCreate.vue'
import DialogActions from '@/components/shared/DialogActions.vue'
import {InstanceContext} from '@/types/InstanceContext'
import {createRoute} from '@/utils/instanceCreation'
import {Preview} from '@/utils/instancePreview'

/**
 * A janela AUXILIAR de criar: a que se abre de dentro de um formulário, pelo "+" de um campo de
 * relacionamento, para criar o outro lado.
 *
 * Ela existe separada da janela da instância por causa do que acontece DEPOIS de salvar: aqui o
 * item criado volta para quem abriu, para ser vinculado ao campo. Levar para a ficha dele —
 * que é a regra quando se cria um item da coleção — perderia o formulário em preenchimento.
 *
 * O formulário em si é o mesmo `InstanceCreate` que a janela da instância usa.
 */
@Component({emits: ['closed', 'saved', 'pending', 'showMessage', 'showErrorMessage'],
  name: 'ontos-create-instance-dialog',
  components: {
    'ontos-instance-create': InstanceCreate,
    'ontos-dialog-actions': DialogActions
  }
})
class CreateInstanceDialog extends Vue {
  @Prop({type: Object, required: true}) readonly context!: InstanceContext
  @Prop({type: String, default: ''}) readonly initialName!: string
  @Prop({type: Object, default: null}) readonly initialValues!: any
  /** Valores com que o item nasce e que não podem sair — ver o `fixedValues` do InstanceCreate. */
  @Prop({type: Object, default: null}) readonly fixedValues!: any
  /** O que quem abriu já leu para o formulário — ver o `known` do InstanceCreate. */
  @Prop({type: Object, default: null}) readonly known!: any
  @Prop({type: Boolean, default: false}) readonly deferred!: boolean
  @Prop({type: Array, default: () => []}) readonly omitProperties!: string[]
  /**
   * Desligado por quem espera a instância de volta — para vincular ao campo que a abriu. Saindo
   * para a página, nada volta.
   */
  @Prop({type: Boolean, default: true}) readonly fullPage!: boolean

  /**
   * Só no criar "limpo". Aberto a partir do "+" de um relacionamento, o formulário já vem
   * semeado — e a página /create-item não receberia nada disso.
   */
  /**
   * Devolve o item criado E a prévia dele — o rascunho com o id, o identificador e os calculados que
   * o formulário já pediu ao servidor. Com ela quem abriu põe o item na tela sem reler (ver o
   * `RelationshipAdd`); o primeiro argumento continua sendo o que o campo de relacionamento usa.
   */
  onSaved(added: any, preview?: Preview) {
    this.$emit('saved', added, preview)
  }

  get fullPageRoute() {
    const seeded = this.deferred || this.initialName || Object.keys(this.initialValues || {}).length
        || Object.keys(this.fixedValues || {}).length || this.omitProperties.length
    if (seeded || !this.fullPage) return null
    return createRoute(this.context.system, this.context.idea)
  }
}

export default toNative(CreateInstanceDialog)
</script>
